// Core

// Chakra
import {
  Box, Code, Table, Tbody, Td,
  Text, Thead, Tr,
} from '@chakra-ui/react';

// Components
import {
  GiveawaysActiveIcon,
} from 'components/Icons/Icons';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import Card from 'components/Card/Card';
import ErrorCard from 'components/Errors';
import { parseDateTime } from '../../../../services/Utils';

const saleObjectFields = [
  { key: 'id', label: 'ID' },
  { key: 'externalId', label: 'External ID' },
  { key: 'storeId', label: 'Store ID' },
  { key: 'terminalId', label: 'Terminal ID' },
  { key: 'requestId', label: 'Request ID' },
  { key: 'timestampUtc', label: 'Timestamp (UTC)' },
  { key: 'saleType', label: 'Sale Type' },
  { key: 'contractLevel', label: 'Contract Level' },
  { key: 'currency', label: 'Currency' },
  { key: 'barcode', label: 'Barcode' },
  { key: 'discountApplied', label: 'Discount Applied' },
  { key: 'grossTotal', label: 'Gross Total' },
  { key: 'discountTotal', label: 'Discount Total' },
  { key: 'netTotal', label: 'Net Total' },
  { key: 'gstTotal', label: 'GST Total' },
  { key: 'createdAt', label: 'Created At' },
];

const PosTransactionDetailsContent = ({ posTransaction, errorCard }) => {

  return (
    <Box
      zIndex="9"
      position="relative"
    >
      <Breadcrumbs
        icon={GiveawaysActiveIcon}
        target="POS Transactions"
        url="#/pos-transactions"
        current="POS Transaction"
        variant="standart"
      />

      {errorCard && <ErrorCard error={errorCard} maxW="840px" />}

      <Box maxW="840px">

        <Text variant="title-lg" mt="40px" mb="14px">
          POS Transaction Details
        </Text>

        <Card padding="24px" mb="40px">

          <Table>
            <Tbody>
              {saleObjectFields.map(({ key, label }) => (
                <Tr key={key}>
                  <Td verticalAlign="top" border="unset">
                    <Text variant="title">{label}</Text>
                  </Td>
                  <Td verticalAlign="top" border="unset">
                    {String(posTransaction?.[key] ?? '')}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Card>

        <Text variant="title-lg" mt="40px" mb="14px">
          POS Transaction Details Payments
        </Text>

        <Card padding="24px" mb="40px">
          <Table>
            <Thead>
              <Tr>
                <Td border="unset">
                  <Text variant="title">Amount</Text>
                </Td>
                <Td border="unset">
                  <Text variant="title">Payment Method</Text>
                </Td>
              </Tr>
            </Thead>

            <Tbody>
              {posTransaction?.payments?.length ? (
                posTransaction.payments.map((payment, index) => (
                  <Tr key={index}>
                    <Td border="unset">
                      {payment.amount}
                    </Td>
                    <Td border="unset">
                      {payment.paymentMethod}
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={2} border="unset">
                    No payments
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Card>

        <Text variant="title-lg" mt="40px" mb="14px">
          POS Transaction Sale Lines
        </Text>

        <Card padding="24px" mb="40px">
          {posTransaction?.saleLines?.length ? (
            posTransaction.saleLines.map((line, index) => (
              <Card key={index} mb="24px" padding="16px" variant="outline">

                <Text variant="title" mb="12px">
                  Line #{line.lineNumber} ({line.lineType})
                </Text>

                <Table>
                  <Tbody>

                    {line.fuel && (
                      <>
                        <Tr>
                          <Td border="unset">Pump Number</Td>
                          <Td border="unset">{line.fuel.pumpNumber}</Td>
                        </Tr>
                        <Tr>
                          <Td border="unset">Hose Number</Td>
                          <Td border="unset">{line.fuel.hoseNumber}</Td>
                        </Tr>
                        <Tr>
                          <Td border="unset">Grade Code</Td>
                          <Td border="unset">{line.fuel.gradeCode}</Td>
                        </Tr>
                        <Tr>
                          <Td border="unset">Volume (Litres)</Td>
                          <Td border="unset">{line.fuel.volumeLitres}</Td>
                        </Tr>
                        <Tr>
                          <Td border="unset">Unit Price</Td>
                          <Td border="unset">{line.fuel.unitPrice}</Td>
                        </Tr>
                        <Tr>
                          <Td border="unset">Gross Amount</Td>
                          <Td border="unset">{line.fuel.grossAmount}</Td>
                        </Tr>
                        <Tr>
                          <Td border="unset">Discount Amount</Td>
                          <Td border="unset">{line.fuel.discountAmount}</Td>
                        </Tr>
                        <Tr>
                          <Td border="unset">Net Amount</Td>
                          <Td border="unset">{line.fuel.netAmount}</Td>
                        </Tr>
                      </>
                    )}

                    {line.item && (
                      <>
                        <Tr>
                          <Td border="unset">PLU</Td>
                          <Td border="unset">{line.item.plu}</Td>
                        </Tr>
                        <Tr>
                          <Td border="unset">Description</Td>
                          <Td border="unset">{line.item.description}</Td>
                        </Tr>
                        <Tr>
                          <Td border="unset">Quantity</Td>
                          <Td border="unset">{line.item.quantity}</Td>
                        </Tr>
                        <Tr>
                          <Td border="unset">Unit Price</Td>
                          <Td border="unset">{line.item.unitPrice}</Td>
                        </Tr>
                        <Tr>
                          <Td border="unset">Gross Amount</Td>
                          <Td border="unset">{line.item.grossAmount}</Td>
                        </Tr>
                        <Tr>
                          <Td border="unset">Discount Amount</Td>
                          <Td border="unset">{line.item.discountAmount}</Td>
                        </Tr>
                        <Tr>
                          <Td border="unset">Net Amount</Td>
                          <Td border="unset">{line.item.netAmount}</Td>
                        </Tr>
                        <Tr>
                          <Td border="unset">GST Amount</Td>
                          <Td border="unset">{line.item.gstAmount}</Td>
                        </Tr>
                      </>
                    )}

                  </Tbody>
                </Table>
              </Card>
            ))
          ) : (
            <Text>No sale lines</Text>
          )}
        </Card>


        {/*<Text variant="title-lg" mt="40px" mb="14px">*/}
        {/*  Details*/}
        {/*</Text>*/}

        {/*<Card padding="24px" mb="40px">*/}
        {/*  <Code*/}
        {/*    p={4}*/}
        {/*    w="100%"*/}
        {/*    mt="24px"*/}
        {/*    whiteSpace="pre"*/}
        {/*    overflowX="auto"*/}
        {/*    colorScheme="gray"*/}
        {/*  >*/}
        {/*    {JSON.stringify(posTransaction, null, 2)}*/}
        {/*  </Code>*/}
        {/*</Card>*/}

      </Box>

    </Box>
  );
};

export default PosTransactionDetailsContent;
